import clsx from 'clsx';
import { forwardRef, type ReactNode } from 'react';

import { label } from './Button.sty.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import { type DataTestId } from '../../core/types/data-props.js';
import { type StylingProps } from '../../core/types/styling-props.js';
import { canReceiveDefaultTextFormat } from '../../core/utils/can-receive-default-text-format.js';

//#region Typings
/**
 * Accepted properties for the Button Label.
 * @public
 */
export interface ButtonLabelProps
  extends StylingProps,
    DataTestId,
    BehaviorTrackingProps {
  /** Elements to be displayed in the Button Label slot. */
  children: ReactNode;
}
//#endregion

/**
 * Button Label component.
 * @public
 */
export const Label = /* @__PURE__ */ forwardRef<
  HTMLDivElement,
  ButtonLabelProps
>((props, forwardedRef) => {
  const {
    children,
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
    ...remainingProps
  }: ButtonLabelProps = props;

  const [behavioralTrackingProps] = useBehavioralTrackingProps(remainingProps);
  const textual = canReceiveDefaultTextFormat(children);

  return (
    <div
      ref={forwardedRef}
      data-testid={dataTestId}
      className={clsx(
        'strato-button-label',
        label({ textual }),
        consumerClassName,
      )}
      style={consumerStyle}
      {...behavioralTrackingProps}
    >
      {children}
    </div>
  );
});

(Label as typeof Label & { displayName: string }).displayName = 'Button.Label';
